import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";

import store, { IAppState } from "../Store";
import Datastore from "../../communication/Datastore";
import YearLevel from "../../types/core/YearLevel";
import { ImportLog } from "../../types/core/ImportLog";
import { unwrappedDispatch } from "../../foundation/Dispatching";

// Define the type for our state store entry
type YearLevelState = {
    archives: YearLevel[];
    yearLevels: YearLevel[];
    status: string;
    error: string;
};

// Define the initial state for our state store entry
const initialState: YearLevelState = {
    archives: [],
    yearLevels: [],
    status: "idle",
    error: null,
};

// Create a slice to hold all our reducers
const yearLevelSlice = createSlice({
    name: "yearLevel",
    initialState: initialState,
    reducers: {
        resetYearLevelStatus(state) {
            state.status = "idle";
        },
    },
    extraReducers(builder) {
        builder
            .addCase(REHYDRATE, (_, rawData: any) => {
                if (rawData?.key !== "tts") return undefined;
                return {
                    archives:
                        rawData?.payload?.yearLevel?.archives?.map(
                            c => new YearLevel(c),
                        ) || [],
                    yearLevels:
                        rawData?.payload?.yearLevel?.yearLevels?.map(
                            c => new YearLevel(c),
                        ) || [],
                    status: !rawData?.payload?.yearLevel?.yearLevels
                        ? "idle"
                        : "succeeded",
                    error: null,
                };
            })
            .addCase(fetchYearLevels.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(fetchYearLevels.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                state.yearLevels = action.payload[0].map(c => new YearLevel(c));
                state.archives = action.payload[1].map(c => new YearLevel(c));
            })
            .addCase(fetchYearLevels.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(addYearLevel.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(addYearLevel.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                state.yearLevels = state.yearLevels.concat(
                    action.payload.map(c => new YearLevel(c)),
                );
            })
            .addCase(addYearLevel.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(updateYearLevel.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(updateYearLevel.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                const yearLevelIds = action.payload.map(c => c.id);
                state.yearLevels = state.yearLevels
                    .filter(c => !yearLevelIds.includes(c.id))
                    .concat(action.payload.map(c => new YearLevel(c)));
            })
            .addCase(updateYearLevel.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(archiveYearLevel.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(archiveYearLevel.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                const yearLevelIds = action.payload.map(c => c.id);
                state.yearLevels = state.yearLevels.filter(
                    c => !yearLevelIds.includes(c.id),
                );
                state.archives = state.archives.concat(
                    action.payload.map(c => new YearLevel(c)),
                );
            })
            .addCase(archiveYearLevel.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            });
    },
});

const { resetYearLevelStatus } = yearLevelSlice.actions;

export const initialiseYearLevelDetails = createAsyncThunk(
    "yearLevel/initialiseYearLevelDetails",
    (_, thunkAPI) => thunkAPI.dispatch(fetchYearLevels()),
);

// This is a thunk, they are used to load the state
export const fetchYearLevels = createAsyncThunk(
    "yearLevel/fetchYearLevels",
    () =>
        Promise.all([
            Datastore.getType<YearLevel[]>("api/YearLevels"),
            Datastore.getType<YearLevel[]>("api/YearLevels/Archive"),
        ]),
    {
        condition: (_, api) =>
            (api.getState() as IAppState).db.yearLevel.status === "idle",
    },
);
export const addYearLevel = createAsyncThunk(
    "yearLevel/addYearLevel",
    (payload: YearLevel[]) =>
        Datastore.postType<YearLevel[]>("api/YearLevels", payload),
    {
        condition: (_, api) =>
            (api.getState() as IAppState).db.yearLevel.status === "succeeded",
    },
);
export const updateYearLevel = createAsyncThunk(
    "yearLevel/updateYearLevel",
    (payload: YearLevel[]) =>
        Datastore.putType<YearLevel[]>("api/YearLevels", payload),
    {
        condition: (_, api) =>
            (api.getState() as IAppState).db.yearLevel.status === "succeeded",
    },
);
export const archiveYearLevel = createAsyncThunk(
    "yearLevel/archiveYearLevel",
    (payload: YearLevel[]) => {
        Datastore.post(
            "api/YearLevels/Archive",
            payload.map(c => c.id),
        );
        return payload;
    },
    {
        condition: (_, api) =>
            (api.getState() as IAppState).db.yearLevel.status === "succeeded",
    },
);

/*******     Importing     *******/
export const importYearLevelsFromTD = async (id: number) => {
    const result = await Datastore.postType<ImportLog>(
        `api/YearLevels/Import/TD/${id}`,
    );

    store.dispatch(resetYearLevelStatus());
    await unwrappedDispatch(fetchYearLevels());

    return result;
};

// We have to export the reducer as this is added to the state store
export default yearLevelSlice.reducer;
